import React, { useEffect, useState } from 'react';
import { GridColumn, GridRow } from 'app/layouts/grid';
import { DetailItem } from 'app/shared/info-section/item';
import { SimpleInput } from 'app/shared/input';
import { MultiSelect } from 'app/shared/select';
import { Icon } from 'assets/icons';
import { DateInput } from '../inputs/date';
import { EventInput } from '../inputs/event';
import styles from '../query.module.css';
import { determineRules } from './helper';

const DATA_TYPES = [
  { label: 'Text', value: 'string' },
  { label: 'Boolean', value: 'boolean' },
  { label: 'Date', value: 'date' },
  { label: 'Event', value: 'event' },
  { label: 'Presence', value: 'exist' },
  { label: 'Number', value: 'number' },
  { label: 'Array', value: 'array' }
];

export const QueryRuleEdit = ({
  data = {},
  properties = [],
  onChange = () => {},
  onDelete = () => {}
}) => {
  const [key, setKey] = useState(data.key);
  const [needle, setNeedle] = useState('');
  const [rules, setRules] = useState([]);
  const [selected_datatype, setSelectedDatatype] = useState({});
  const [selected_property, setSelectedProperty] = useState({});
  const [selected_rule, setSelectedRule] = useState({});

  useEffect(() => {
    setKey(() => data.key);
    if (!data || !Object.keys(data).length) return;
    const possible_rules = determineRules(data.datatype);

    const selected_dtype = DATA_TYPES.find((type) => type.value === data.datatype);
    setSelectedDatatype(() => selected_dtype);

    if (selected_dtype?.value) setRules(() => determineRules(selected_dtype?.value));
    setSelectedRule(() => possible_rules.find((rule) => rule.value === data.rule));
    setSelectedProperty(() => properties.find((prop) => prop.value === data.property));
    setNeedle(() => data.needle);
  }, []);

  useEffect(() => {
    if (!selected_property?.value || !selected_datatype?.value || !selected_rule?.value) return;
    if (!['boolean', 'exist'].includes(selected_datatype?.value) && !needle) return;

    const query = {
      key,
      datatype: selected_datatype?.value,
      property: selected_property?.value,
      rule: selected_rule?.value,
      kind: selected_property?.kind,
      needle: determineNeedle(
        selected_property?.value,
        selected_datatype?.value,
        selected_rule?.value,
        needle
      )
    };

    onChange(query);
  }, [selected_property?.value, selected_datatype?.value, selected_rule?.value, needle]);

  const determineNeedle = (property, datatype, rule, needle) => {
    let value = needle;
    if (datatype === 'boolean' || datatype === 'exist') {
      value = rule;
    }

    value = property === 'last_action' ? value?.split(':')[0] : value;
    return value;
  };

  const handlePropertyChange = (selection) => {
    setSelectedProperty(selection);
    setSelectedDatatype({});
    setSelectedRule({});
    setNeedle('');
  };

  const handleDatatypeChange = (selection) => {
    if (!selection?.value) return;
    const possible_rules = determineRules(selection?.value);
    const default_rule = selected_property?.value === 'last_action' ? {} : possible_rules[0];

    setSelectedDatatype(selection);

    setRules(() => possible_rules);
    setSelectedRule(() => default_rule);
    setNeedle(() => '');
  };

  const handleRuleChange = (selection) => {
    setSelectedRule(() => selection);
    setNeedle(() => '');
  };

  const handleNeedleChange = (value) => {
    setNeedle(() => value);
  };

  return (
    <GridRow num_of_columns={5}>
      <GridColumn>
        <DetailItem title="property">
          <MultiSelect
            options={properties}
            value={selected_property}
            onChange={handlePropertyChange}
          />
        </DetailItem>
      </GridColumn>
      <GridColumn>
        <DetailItem title="data type">
          <MultiSelect
            options={DATA_TYPES}
            value={selected_datatype}
            onChange={handleDatatypeChange}
            disabled={['last_action', 'last_action_time'].includes(selected_property?.value)}
          />
        </DetailItem>
      </GridColumn>
      <GridColumn>
        <DetailItem title="rule">
          <MultiSelect
            options={rules}
            value={selected_rule}
            onChange={handleRuleChange}
            isSorted={false}
            disabled={selected_property?.value === 'last_action'}
          />
        </DetailItem>
      </GridColumn>
      <GridColumn span={2}>
        <GridRow num_of_columns={10}>
          <GridColumn span={9}>
            {selected_datatype?.value !== 'date' ? (
              selected_datatype?.value !== 'event' &&
              !['last_action'].includes(selected_property?.value) ? (
                <DetailItem title="value">
                  <SimpleInput
                    type={selected_datatype?.value === 'number' ? 'number' : 'text'}
                    disabled={['boolean', 'exist'].includes(selected_datatype?.value)}
                    value={needle}
                    onInput={handleNeedleChange}
                  />
                </DetailItem>
              ) : (
                <EventInput value={needle} onChange={handleNeedleChange} />
              )
            ) : (
              <DateInput rule={selected_rule?.value} value={needle} onChange={handleNeedleChange} />
            )}
          </GridColumn>
          <GridColumn>
            <div className={styles.icon}>
              <Icon name="trash" onClick={() => onDelete(key)} />
            </div>
          </GridColumn>
        </GridRow>
      </GridColumn>
    </GridRow>
  );
};
